import { isNumber } from 'lodash';
import {
  type ChangeEventHandler,
  type KeyboardEventHandler,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Input } from '@air/primitive-input';

type PDFCurrentPageInputProps = {
  currentPage: number;
  onGoToPage: (pageNumber: number) => void;
  totalPages: number;
  onFocusChange: (isFocused: boolean) => void;
};

export const PDFCurrentPageInput = memo(
  ({ onGoToPage, currentPage, totalPages, onFocusChange }: PDFCurrentPageInputProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [value, setValue] = useState<string>(currentPage.toString());

    useEffect(() => {
      setValue(currentPage.toString());
    }, [currentPage]);

    const onValueChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
      setValue(event.target.value);
    }, []);

    const setNewPageFromInput = useCallback(() => {
      if (value === currentPage.toString()) return;
      const newPage = Number(value);

      if (!isNumber(newPage)) return;

      if (newPage >= 1 && newPage <= totalPages) {
        onGoToPage(newPage);
      } else {
        setValue(currentPage.toString());
      }
    }, [currentPage, onGoToPage, totalPages, value]);

    const onFocus = useCallback(() => {
      inputRef.current?.select();
      onFocusChange(true);
    }, [onFocusChange]);

    const onBlur = useCallback(() => {
      setNewPageFromInput();
      onFocusChange(false);
    }, [onFocusChange, setNewPageFromInput]);

    const onKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
      (event) => {
        if (event.key === 'Enter') {
          setNewPageFromInput();
        }
      },
      [setNewPageFromInput],
    );

    return (
      <Input
        data-testid="PDF_CURRENT_PAGE_INPUT"
        ref={inputRef}
        onBlur={onBlur}
        onFocus={onFocus}
        size="small"
        type="number"
        value={value}
        onKeyDown={onKeyDown}
        onChange={onValueChange}
        min={1}
        max={totalPages}
        className="w-8 text-end [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
    );
  },
);

PDFCurrentPageInput.displayName = 'PDFCurrentPageInput';
